import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Users, Plus, Search, Loader2, Mail, Phone, MapPin, Building2, Trash2, Save, X,
} from "lucide-react";
import { cn } from "@/lib/utils";

const empty = {
  name: "", firstname: "", company: "", email: "", phone: "", mobile: "",
  address_line: "", postal_code: "", city: "", country: "Deutschland",
  vat_id: "", notes: "",
};

export default function Kunden() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async (search = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers${search ? `?q=${encodeURIComponent(search)}` : ""}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(q.trim());
  };

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...empty, ...c });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Name ist Pflichtfeld");
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/customers/${editing.id}`, form);
        toast.success("Kunde aktualisiert");
      } else {
        await api.post("/customers", form);
        toast.success("Kunde angelegt");
      }
      setOpen(false);
      load(q.trim());
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Kunden „${c.firstname || ""} ${c.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Kunde gelöscht");
      load(q.trim());
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Layout
      title="Kunden"
      subtitle="Stammdatenbank deiner Auftraggeber"
      action={
        <Button onClick={openCreate} data-testid="customer-create-btn">
          <Plus className="h-4 w-4 mr-2" /> Neuer Kunde
        </Button>
      }
      primaryAction={{ label: "Neuer Kunde", icon: Plus, onClick: openCreate, testid: "mobile-fab-new-customer" }}
    >
      <div className="max-w-6xl space-y-5">
        <form onSubmit={onSearch} className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suche Name, Firma, Stadt, E-Mail …"
            className="pl-10"
            data-testid="customer-search"
          />
        </form>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>
        ) : items.length === 0 ? (
          <Card className="p-12 text-center" data-testid="customer-empty">
            <div className="h-14 w-14 rounded-2xl brand-gradient-bg flex items-center justify-center mx-auto mb-4">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div className="font-display text-lg font-bold">Noch keine Kunden</div>
            <p className="text-sm text-zinc-500 mt-1 mb-5">Lege deinen ersten Kunden an, um Akten direkt zuordnen zu können.</p>
            <Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" /> Ersten Kunden anlegen</Button>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="customer-grid">
            {items.map((c) => {
              const initials = `${c.firstname?.[0] || ""}${c.name?.[0] || ""}`.toUpperCase() || "?";
              const subtitle = [c.company, c.city].filter(Boolean).join(" · ");
              return (
                <Card
                  key={c.id}
                  className="p-5 hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => openEdit(c)}
                  data-testid={`customer-card-${c.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-12 w-12 rounded-full brand-gradient-bg text-white font-display font-bold flex items-center justify-center shrink-0">
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold tracking-tight truncate">
                        {[c.firstname, c.name].filter(Boolean).join(" ")}
                      </div>
                      {subtitle && <div className="text-xs text-zinc-500 truncate mt-0.5">{subtitle}</div>}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); remove(c); }}
                      className="opacity-0 group-hover:opacity-100 transition text-rose-500 hover:bg-rose-50 rounded-md p-1"
                      data-testid={`customer-delete-${c.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-3 space-y-1.5 text-[12px] text-zinc-600">
                    {c.email && <div className="flex items-center gap-2 truncate"><Mail className="h-3 w-3 shrink-0 text-zinc-400" /> {c.email}</div>}
                    {(c.mobile || c.phone) && <div className="flex items-center gap-2 truncate"><Phone className="h-3 w-3 shrink-0 text-zinc-400" /> {c.mobile || c.phone}</div>}
                    {(c.postal_code || c.city) && <div className="flex items-center gap-2 truncate"><MapPin className="h-3 w-3 shrink-0 text-zinc-400" /> {[c.postal_code, c.city].filter(Boolean).join(" ")}</div>}
                    {c.company && <div className="flex items-center gap-2 truncate"><Building2 className="h-3 w-3 shrink-0 text-zinc-400" /> {c.company}</div>}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Kunden bearbeiten" : "Neuer Kunde"}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Vorname"><Input value={form.firstname || ""} onChange={(e) => setForm({ ...form, firstname: e.target.value })} data-testid="customer-form-firstname" /></Field>
            <Field label="Nachname *"><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-form-name" required /></Field>
            <div className="sm:col-span-2"><Field label="Firma"><Input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="customer-form-company" /></Field></div>
            <Field label="E-Mail"><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-form-email" /></Field>
            <Field label="USt-IdNr."><Input value={form.vat_id || ""} onChange={(e) => setForm({ ...form, vat_id: e.target.value.toUpperCase() })} data-testid="customer-form-vat" /></Field>
            <Field label="Telefon"><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-form-phone" /></Field>
            <Field label="Mobil"><Input value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="customer-form-mobile" /></Field>
            <div className="sm:col-span-2"><Field label="Straße & Hausnummer"><Input value={form.address_line || ""} onChange={(e) => setForm({ ...form, address_line: e.target.value })} data-testid="customer-form-address" /></Field></div>
            <Field label="PLZ"><Input value={form.postal_code || ""} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="customer-form-postal" /></Field>
            <Field label="Stadt"><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="customer-form-city" /></Field>
            <div className="sm:col-span-2"><Field label="Land"><Input value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="customer-form-country" /></Field></div>
            <div className="sm:col-span-2"><Field label="Notizen"><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="customer-form-notes" /></Field></div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}><X className="h-4 w-4 mr-2" /> Abbrechen</Button>
            <Button onClick={save} disabled={saving} data-testid="customer-form-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {editing ? "Speichern" : "Anlegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
